'use client';

import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

/**
 * The drawing is the article, so it opens in a full-screen modal.
 *
 * Colours here are inline rather than Tailwind opacity utilities. The theme
 * defines its colours as bare CSS variables (`var(--foreground)`) with no
 * <alpha-value> placeholder, so `bg-foreground/90` compiles to an invalid
 * colour and renders as nothing. That is why the first version opened a modal
 * with no backdrop at all.
 */
export default function SketchViewer({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group block w-full cursor-zoom-in border-0 bg-transparent p-0 text-left"
        aria-label={`Open full size: ${alt}`}
      >
        <Image
          src={src}
          alt={alt}
          width={1600}
          height={1000}
          priority
          sizes="(max-width: 900px) 100vw, 900px"
          className="h-auto w-full rounded-sm transition-shadow group-hover:shadow-lg"
          style={{ border: '1px solid rgba(24,49,83,0.12)' }}
        />
        <span
          className="mt-2 block text-right text-xs"
          style={{ color: 'rgba(24,49,83,0.55)' }}
        >
          Open full size
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            background: 'rgba(12, 24, 41, 0.94)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(16px, 4vw, 48px)',
            cursor: 'zoom-out',
          }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: '#FFFDF7',
              color: '#183153',
              border: 'none',
              borderRadius: 999,
              padding: '8px 18px',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Close
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '100%', cursor: 'default' }}
          >
            <Image
              src={src}
              alt={alt}
              width={1600}
              height={1000}
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: caption ? 'calc(100vh - 160px)' : 'calc(100vh - 110px)',
                background: '#FFFDF7',
                borderRadius: 2,
              }}
            />
            {caption && (
              <p
                style={{
                  marginTop: 14,
                  textAlign: 'center',
                  color: 'rgba(255,253,247,0.75)',
                  fontSize: 14,
                }}
              >
                {caption}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
